import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatExact } from '../utils/format';
import './ShellFilterBar.css';
import { IcoLn, lineText } from '../shell/ActionIcons';

import { emoText } from '../shell/EmojiIcons';
// v0.43 shared Filter bar. One collapsible/customizable filter shell for every list
// (GL, Orders, Reports, Inventory). Mirrors the approved design in the foundations guide
// (#filters). Presentational: the caller owns filter STATE and renders the controls via
// the `children` render-prop; this component owns the chrome (collapse, clear-all, summary
// chips, Customize show/hide, saved views) and persists collapse+visibility to localStorage.
//
// Props:
//   storageKey  string  — namespace for persisted collapse + hidden-control state
//   count       number  — active-filter count (caller computes); drives the badge + Clear all
//   onClearAll  fn      — reset every filter to default
//   customize   [{key,label}]           — controls the user may hide/show (order = display order)
//   summary     [{key,label,onRemove?,undo?:{label,title,onClick}}] — chips shown when collapsed (one per active filter); undo = a one-click revert beside the label (A7j scope switch)
//   views       { items:[{id,label}], activeId, onSelect(id), onSave(), onDelete(id) }  (optional)
//   children    (visSet:Set<string>) => node — body; render each control only if visSet.has(key)

export const read = (k, fb) => { try { const v = localStorage.getItem(k); return v == null ? fb : JSON.parse(v); } catch { return fb; } };
export const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* quota */ } };

// `fixed` = the General-Journal-style plain filter card: no ⛃ Filters head, no Collapse/Clear-all/
// Customize chrome, body always shown. Used where the filter is a simple always-visible period/level
// control (the financial statements) rather than a rich collapsible filter set.
export default function ShellFilterBar({ storageKey = 'fb', count = 0, onClearAll, customize = [], summary = [], views, scope, scopePlacement = 'top', fixed = false, meta = null, actions = null, children }) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(() => read(`${storageKey}.collapsed`, false));
  const [hidden, setHidden] = useState(() => read(`${storageKey}.hidden`, []));
  const [custOpen, setCustOpen] = useState(false);
  const custRef = useRef(null);

  useEffect(() => write(`${storageKey}.collapsed`, collapsed), [collapsed, storageKey]);
  useEffect(() => write(`${storageKey}.hidden`, hidden), [hidden, storageKey]);

  useEffect(() => {
    if (!custOpen) return;
    const onDoc = (e) => { if (custRef.current && !custRef.current.contains(e.target)) setCustOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [custOpen]);

  const hiddenSet = useMemo(() => new Set(hidden), [hidden]);
  const visSet = useMemo(() => new Set(customize.filter(c => !hiddenSet.has(c.key)).map(c => c.key)), [customize, hiddenSet]);
  // keys not declared in `customize` are always visible (not user-hideable)
  const custKeys = useMemo(() => new Set(customize.map(c => c.key)), [customize]);
  const isVisible = useCallback((key) => custKeys.has(key) ? visSet.has(key) : true, [visSet, custKeys]);
  // expose a Set-like with an isVisible-aware .has for the render prop
  const vis = { has: isVisible };
  const hasBody = children != null && children !== false;

  const toggleHidden = (key) => setHidden(h => h.includes(key) ? h.filter(k => k !== key) : [...h, key]);

  return (
    <div className="avp-fbar">
      {views && (
        <div className="fb-views">
          <span className="fb-eyebrow">{t('fb.views')}</span>
          {views.items.map(v => (
            <button key={v.id} className={`fb-view${views.activeId === v.id ? ' on' : ''}`} onClick={() => views.onSelect?.(v.id)}>
              {lineText(v.label)}
              {views.onDelete && v.id !== '__all__' && (
                <span className="vx" title={t('fb.deleteView')} onClick={(e) => { e.stopPropagation(); views.onDelete(v.id); }}><IcoLn name="close" /></span>
              )}
            </button>
          ))}
          {views.onSave && <button className="fb-view add" onClick={() => views.onSave()}><IcoLn name="add" /> {t('fb.saveView')}</button>}
        </div>
      )}

      <div className="fbar">
        {/* fb-191/199: scopePlacement='belowTitle' (General Journal only) draws the Filters header first, then the scope row. Default 'top' = unchanged for every other page. */}
        {scope && !(scopePlacement === 'belowTitle' && hasBody && !fixed) && <div className="fbar-scope">{scope}</div>}
        {hasBody && !fixed && (
          <div className="fbar-head">
            <span className="fbar-title"><IcoLn name="filter" /> {t('fb.filters')}{count > 0 && <span className="fbar-count">{formatExact(String(count))}</span>}</span>
            {summary.length > 0 && (
              <div className="fb-sum" data-testid="fb-active-chips">
                {summary.map(s => (
                  <span key={s.key} className="fb-sc">
                    {emoText(s.label)}
                    {s.undo && <button type="button" className="fb-undo" title={s.undo.title} data-testid="fb-chip-undo" onClick={s.undo.onClick}>{s.undo.label}</button>}
                    {s.onRemove && <button type="button" className="x" title={t('fb.remove')} aria-label={t('fb.remove')} onClick={s.onRemove}
                      onKeyDown={e => { if (e.key === 'Escape' || e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); s.onRemove(); } }}><IcoLn name="close" size={12} /></button>}
                  </span>
                ))}
                {count > 0 && onClearAll && <button type="button" className="fbar-btn fbar-clear" onClick={onClearAll}>{t('fb.clearAll')}</button>}
              </div>
            )}
            <span style={{ flex: 1 }} />
            {meta && <span className="tbl-meta">{meta}</span>}
            {count > 0 && onClearAll && !summary.length && <button className="fbar-btn fbar-clear" onClick={onClearAll}>{t('fb.clearAll')}</button>}
            {customize.length > 0 && (
              <div className={`fb-cust${custOpen ? ' open' : ''}`} ref={custRef}>
                <button className="fbar-btn" onClick={() => setCustOpen(o => !o)}><IcoLn name="gear" /> {t('fb.customize')}</button>
                <div className="fb-cust-pop">
                  <div className="h">{t('fb.showControls')}</div>
                  {customize.map(c => (
                    <label key={c.key}>
                      <input type="checkbox" checked={!hiddenSet.has(c.key)} onChange={() => toggleHidden(c.key)} />
                      {emoText(c.label)}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {actions}
            <button className="fbar-btn" onClick={() => setCollapsed(c => !c)}>
              {collapsed ? `${t('fb.expand')} ▾` : `${t('fb.collapse')} ▴`}
            </button>
          </div>
        )}
        {scope && scopePlacement === 'belowTitle' && hasBody && !fixed && <div className="fbar-scope below-title">{scope}</div>}
        {hasBody && (
          <div className={`fbar-body${collapsed && !fixed ? ' hide' : ''}`}>
            {typeof children === 'function' ? children(vis) : children}
          </div>
        )}
      </div>
    </div>
  );
}

// Per-type facet dropdown (.fb-md) — THE shared multi-select. A multi-select of one type's values with an
// (on/total) count. `options`=[{value,label,group?}]; `isOff(v)` = excluded. Layout (fb-208): search box pinned
// at the top (>8 options), ONLY the option list scrolls, the All / None footer is pinned at the bottom.
// All / None act on the VISIBLE (search-filtered) options. Bulk handler, in priority order:
//   onSet(values, on) > per-value onToggle loop (functional-setState callers) > whole-set onAll/onNone.
// Esc / click-outside close; focus returns to the trigger. Footer shows when the caller wires any bulk handler.
export function FacetMenu({ label, options, isOff, onToggle, onAll, onNone, onSet, onOpenChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const btn = useRef(null);
  const close = useCallback((refocus) => { setOpen(false); setQ(''); if (refocus) btn.current?.focus(); }, []);
  useEffect(() => { onOpenChange?.(open); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) close(false); };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); close(true); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open, close]);
  const onCount = options.filter(o => !isOff(o.value)).length;
  const act = onCount < options.length;
  // fb-166: long lists (accounts, partners, bands...) get a search box inside the popup.
  const ql = q.trim().toLowerCase();
  const shown = ql ? options.filter(o => String(o.label).toLowerCase().includes(ql)) : options;
  const filtered = shown.length !== options.length;
  // vals = the option values to act on (visible ones); on = tick (true) / untick (false)
  const setMany = (vals, on) => {
    if (onSet) return onSet(vals, on);
    if (vals.length !== options.length) return vals.forEach(v => { if (isOff(v) === on) onToggle(v); });
    return on ? onAll?.() : onNone?.();
  };
  const bulk = (on) => setMany(shown.map(o => o.value), on);
  // Group / subgroup header: tri-state over the VISIBLE options under it (all ticked -> untick them, else tick them).
  const GroupHead = ({ cls, text, from, to }) => {
    const vals = shown.slice(from, to).map(o => o.value);
    const n = vals.filter(v => !isOff(v)).length;
    const total = shown.slice(from, to).reduce((a, o) => a + (o.count == null ? NaN : o.count), 0);
    return (
      <button type="button" className={`${cls}${n === vals.length ? ' all' : n ? ' some' : ''}`} aria-pressed={n === vals.length ? true : n ? 'mixed' : false}
        title={t('ms.toggleGroup')} onClick={() => setMany(vals, n !== vals.length)}>
        <span className="fb-md-gbox" aria-hidden="true">{lineText(n === vals.length ? ":check:" : n ? '–' : '')}</span>{lineText(text)}
        {Number.isFinite(total) && <span className="fb-md-hint"> ({total})</span>}
      </button>
    );
  };
  // run length of options sharing the same group (and subgroup) starting at i
  const runEnd = (i, key) => { let j = i + 1; while (j < shown.length && shown[j].group === shown[i].group && (key === 'group' || shown[j].subgroup === shown[i].subgroup)) j++; return j; };
  const onPopKey = (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const els = [...e.currentTarget.querySelectorAll('input,button.fb-md-grp,button.fb-md-sub,.fb-md-foot button')];
    const i = els.indexOf(document.activeElement);
    if (i < 0) return;
    e.preventDefault();
    els[Math.max(0, Math.min(els.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1)))].focus();
  };
  const hasBulk = !!(onSet || onAll || onNone);
  return (
    <div className={`fb-md${open ? ' open' : ''}${act ? ' act' : ''}`} ref={ref}>
      <button type="button" ref={btn} aria-haspopup="listbox" aria-expanded={open} onClick={() => (open ? close(false) : setOpen(true))}>{lineText(label)}<span className="n">({formatExact(String(onCount))}/{formatExact(String(options.length))})</span></button>
      <div className="fb-md-pop" role="listbox" aria-multiselectable="true" aria-label={label} onKeyDown={onPopKey}>
        {options.length > 8 && (
          <input className="fb-md-q" value={q} onChange={e => setQ(e.target.value)} placeholder={t('ms.search')} aria-label={t('ms.search')} autoFocus={open} />
        )}
        <div className="fb-md-list">
          {shown.map((o, i) => (
            <React.Fragment key={o.value}>
              {/* fb-190 + groups: group (+ optional subgroup) headings; clickable tri-state toggles for the options under them */}
              {o.group && (i === 0 || shown[i - 1].group !== o.group) && <GroupHead cls="fb-md-grp" text={o.group} from={i} to={runEnd(i, 'group')} />}
              {o.subgroup && (i === 0 || shown[i - 1].group !== o.group || shown[i - 1].subgroup !== o.subgroup) && <GroupHead cls="fb-md-sub" text={o.subgroup} from={i} to={runEnd(i, 'sub')} />}
              <label title={o.title} className={[o.subgroup ? 'ind2' : o.group ? 'ind1' : '', o.disabled ? 'dis' : '', o.grey && !o.disabled ? 'zero' : ''].filter(Boolean).join(' ') || undefined}>
                <input type="checkbox" checked={!isOff(o.value)} disabled={!!o.disabled} onChange={() => onToggle(o.value)} />
                {lineText(o.label)}{o.hint ? <span className="fb-md-hint"> {o.hint}</span> : null}
              </label>
            </React.Fragment>
          ))}
          {!shown.length && <div className="fb-md-empty">{t('ms.noMatch')}</div>}
        </div>
        {hasBulk && (
          <div className="fb-md-foot" title={filtered ? t('ms.visibleOnly') : undefined}>
            <button type="button" onClick={() => bulk(true)}>{filtered ? t('ms.allVisible') : t('ms.all')}</button>
            <button type="button" onClick={() => bulk(false)}>{filtered ? t('ms.noneVisible') : t('ms.none')}</button>
          </div>
        )}
      </div>
    </div>
  );
}

// fb-166: include-style adapter over FacetMenu for filters whose state is "the values to KEEP" (empty = everything).
// `sel` = array of kept values; the menu shows every option ticked while nothing is selected.
// fb-208: None un-ticks everything (the menu then shows all unticked until the user picks values; a still-empty
// selection when the menu closes means "no restriction", as before). All / None act on the visible options.
export function MultiFacet({ label, options, sel, onChange, keepEmpty = false }) {
  const [none, setNone] = useState(false);
  if (!keepEmpty && !options.length && !sel.length) return null;   // nothing to choose from under the other filters (keepEmpty: stay visible, e.g. cross-filtering pages)
  const all = options.map(o => o.value);
  const on = sel.length ? new Set(sel) : (none ? new Set() : new Set(all));
  const commit = (next) => {
    const full = all.every(v => next.has(v));
    setNone(next.size === 0);
    // 2nd argument: the options newly ticked relative to what the menu showed ticked (none when unticking from 'everything ticked'); lets a scope-aware page tell a pick from an untick
    onChange(next.size === 0 || full ? [] : [...all.filter(v => next.has(v)), ...sel.filter(v => !all.includes(v))], { added: all.filter(v => next.has(v) && !on.has(v)) });
  };
  return (
    <FacetMenu
      label={label}
      options={options}
      isOff={v => !on.has(v)}
      onToggle={v => { const next = new Set(on); next.has(v) ? next.delete(v) : next.add(v); commit(next); }}
      onSet={(vals, tick) => { const next = new Set(on); vals.forEach(v => (tick ? next.add(v) : next.delete(v))); commit(next); }}
      onOpenChange={o => { if (!o) setNone(false); }}
    />
  );
}

// Single-select sibling of MultiFacet (same .fb-md popover: search >8 options, group/subgroup headings, Esc / click-outside).
// `value` '' = All (shown as `allLabel`); picking an option calls onChange(value) and closes. Use for fields/filters that
// must stay single-valued (one band / item / storage).
// FORM mode (`bare`): replaces a native <select> in a create form. No "label:" prefix on the trigger (it shows the picked
// name or `placeholder`), no All row unless allowAll, `required` blocks native form submit via an invisible required input,
// `disabled` works, the popover is position:fixed (not clipped by drawers / scrolling table wrappers), and focus returns
// to the trigger after a pick so the form's tab order is kept. Arrow keys move through search/options; Enter picks.
export function SingleFacet({ label, options, value, onChange, allLabel, allowAll = true, bare = false, placeholder, required = false, disabled = false, btnStyle, wrapStyle, invalid = false, ariaLabel, title, testid, id, searchAbove = 8, dflt = '' }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const btn = useRef(null);
  const pop = useRef(null);
  const shut = useCallback((refocus) => { setOpen(false); setQ(''); if (refocus) btn.current?.focus(); }, []);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) shut(false); };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); shut(true); } };
    const onMove = (e) => { if (bare && !(e.target instanceof Node && pop.current?.contains(e.target))) shut(false); };   // 47233: a window resize has no Node target (contains() threw)
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey);
    if (bare) { window.addEventListener('scroll', onMove, true); window.addEventListener('resize', onMove); }
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); window.removeEventListener('scroll', onMove, true); window.removeEventListener('resize', onMove); };
  }, [open, bare, shut]);
  const toggle = () => {
    if (disabled) return;
    if (!open && bare && btn.current) {
      const r = btn.current.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - 8;
      const up = below < 200 && r.top > below;
      const room = Math.max(120, (up ? r.top : below) - 4);
      setPos({ position: 'fixed', left: Math.max(4, Math.min(r.left, window.innerWidth - Math.min(340, 0.92 * window.innerWidth) - 4)), minWidth: r.width, maxHeight: Math.min(340, room), zIndex: 2000, ...(up ? { bottom: window.innerHeight - r.top + 4, top: 'auto' } : { top: r.bottom + 4 }) });
    }
    setOpen(o => !o); setQ('');
  };
  const ql = q.trim().toLowerCase();
  const shown = ql ? options.filter(o => String(o.label).toLowerCase().includes(ql)) : options;
  const cur = options.find(o => o.value === value);
  const pick = (v) => { onChange(v); shut(bare); };
  const heads = (o, i) => (<>
    {o.group && (i === 0 || shown[i - 1].group !== o.group) && <div className="fb-md-grp">{emoText(o.group)}</div>}
    {o.subgroup && (i === 0 || shown[i - 1].group !== o.group || shown[i - 1].subgroup !== o.subgroup) && <div className="fb-md-sub">{emoText(o.subgroup)}</div>}
  </>);
  // Single-select rows: NO native radio. Option = div[role=option] (selected = highlighted row + check glyph at the end).
  const row = (v, text, o = {}) => {
    const sel = value === v, dis = !!o.disabled;
    return (
      <div key={v} role="option" data-value={v} tabIndex={-1} aria-selected={sel} aria-disabled={dis || undefined} title={o.title || (typeof text === 'string' ? text : undefined)}
        className={['fb-opt', sel ? 'sel' : '', o.subgroup ? 'ind2' : o.group ? 'ind1' : '', dis ? 'dis' : ''].filter(Boolean).join(' ')}
        onClick={() => { if (!dis) pick(v); }}>
        <span className="fb-opt-t">{emoText(text)}{o.hint ? <span className="fb-md-hint"> {o.hint}</span> : null}</span>
        {sel && <span className="fb-opt-ck" aria-hidden="true">{lineText(':check:')}</span>}
      </div>);
  };
  const onPopKey = (e) => {
    const els = [...e.currentTarget.querySelectorAll('input.fb-md-q,[role=option]:not([aria-disabled])')];
    const i = els.indexOf(document.activeElement);
    if ((e.key === 'Enter' || e.key === ' ') && i >= 0 && els[i].getAttribute('role') === 'option') { e.preventDefault(); els[i].click(); return; }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const cur = i < 0 ? els.findIndex(x => x.getAttribute('aria-selected') === 'true') : i;
    els[Math.max(0, Math.min(els.length - 1, (cur < 0 ? 0 : cur) + (i < 0 ? 0 : e.key === 'ArrowDown' ? 1 : -1)))]?.focus();
  };
  const text = cur ? cur.label : (value ? String(value) : (bare && placeholder != null ? placeholder : (allLabel || t('ms.all'))));
  return (
    <div className={`fb-md${open ? ' open' : ''}${invalid ? ' bad' : ''}${value && value !== dflt && !bare ? ' act' : ''}`} ref={ref} style={bare ? { display: 'block', width: '100%', ...wrapStyle } : wrapStyle}>
      <button type="button" ref={btn} id={id} data-testid={testid} aria-label={ariaLabel} data-value={value == null ? '' : String(value)} title={title} disabled={disabled} aria-haspopup="listbox" aria-expanded={open} onClick={toggle}
        style={bare ? { width: '100%', textAlign: 'left', fontWeight: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: cur ? undefined : '#6a786e', ...btnStyle } : undefined}>
        {bare ? lineText(text) : <>{lineText(label)}<span className="n">: {text}</span></>}
      </button>
      {required && <input tabIndex={-1} aria-hidden="true" required value={value || ''} onChange={() => {}} style={{ position: 'absolute', left: 0, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />}
      <div className="fb-md-pop" ref={pop} role="listbox" aria-label={label} style={bare && open ? pos || undefined : undefined} onKeyDown={onPopKey}>
        {options.length > searchAbove && <input className="fb-md-q" value={q} onChange={e => setQ(e.target.value)} placeholder={t('ms.search')} aria-label={t('ms.search')} autoFocus={open} />}
        <div className="fb-md-list">
          {allowAll && !ql && row('', allLabel || (bare && placeholder != null ? placeholder : t('ms.all')))}
          {shown.map((o, i) => <React.Fragment key={o.value}>{heads(o, i)}{row(o.value, o.label, o)}</React.Fragment>)}
          {!shown.length && <div className="fb-md-empty">{t('ms.noMatch')}</div>}
        </div>
      </div>
    </div>
  );
}

// A7j: the SCOPE control (Open | Closed | All) as the first item of a filter bar. Same widget family as the date-basis control (.pseg, shell.css). options = [{ value, label, count? }]; a missing
// count (not answered yet) shows the label alone. Lists with one lifecycle scope use it with lib facetModel (scopedOption / pickScope); pages without a scope never render it.
export function ScopeSeg({ value, options, onChange, label, testid = 'scope' }) {
  return (
    <span className="pseg" role="group" aria-label={label} data-testid={`${testid}-seg`}>
      {options.map(o => (
        <button key={o.value} type="button" className={value === o.value ? 'on' : undefined} aria-pressed={value === o.value} data-testid={`${testid}-${o.value}`} onClick={() => onChange(o.value)}>
          {o.label}{o.count == null ? '' : ` (${formatExact(String(o.count))})`}
        </button>
      ))}
    </span>
  );
}
